import { useCallback, useEffect, useRef, useState } from "react";

import { getIdToken } from "@/lib/auth";

import { streamChatMessage } from "../api/assistant-api";
import type { ChatError, ChatMessage } from "../types";

interface UseChatSessionReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  isStreaming: boolean;
  error: ChatError | null;
  sessionId: string;
  activeModelId: string | null;
  activeModelName: string | null;
  sendMessage: (content: string) => void;
  resetSession: () => void;
}

function generateId(): string {
  return crypto.randomUUID();
}

function isAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === "AbortError") return true;
  if (err instanceof Error && err.name === "AbortError") return true;
  return false;
}

export function useChatSession(): UseChatSessionReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);
  const [activeModelId, setActiveModelId] = useState<string | null>(null);
  const [activeModelName, setActiveModelName] = useState<string | null>(null);
  const sessionIdRef = useRef<string>(generateId());
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessageHandler = useCallback((content: string) => {
    const trimmed = content.trim();
    if (!trimmed) return;

    const userMessage: ChatMessage = {
      id: generateId(),
      role: "user",
      content: trimmed,
      timestamp: Date.now(),
    };

    const assistantMessageId = generateId();

    setMessages((prev) => [...prev, userMessage]);
    setError(null);
    setIsLoading(true);

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    getIdToken()
      .then((token) => {
        const assistantMessage: ChatMessage = {
          id: assistantMessageId,
          role: "assistant",
          content: "",
          timestamp: Date.now(),
        };
        setMessages((prev) => [...prev, assistantMessage]);
        setIsLoading(false);
        setIsStreaming(true);

        return streamChatMessage(
          {
            message: trimmed,
            sessionId: sessionIdRef.current,
            authToken: token,
          },
          {
            onToken: (token) => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId ? { ...msg, content: msg.content + token } : msg
                )
              );
            },
            onDone: (response) => {
              setActiveModelId(response.model_id);
              setActiveModelName(response.model_name);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? {
                        ...msg,
                        content: response.message,
                        modelId: response.model_id,
                        ...(response.actions ? { actions: response.actions } : {}),
                      }
                    : msg
                )
              );
              setIsStreaming(false);
            },
            onError: (err) => {
              setMessages((prev) => prev.filter((msg) => msg.id !== assistantMessageId));
              const chatError: ChatError = { message: err.message };
              if (
                "status" in err &&
                typeof (err as Error & { status?: number }).status === "number"
              ) {
                chatError.status = (err as Error & { status: number }).status;
              }
              setError(chatError);
              setIsStreaming(false);
            },
          },
          abortController.signal
        );
      })
      .catch((err: unknown) => {
        if (isAbortError(err)) {
          setIsLoading(false);
          setIsStreaming(false);
          return;
        }
        setMessages((prev) => prev.filter((msg) => msg.id !== assistantMessageId));
        const apiError = err as Error & { status?: number };
        const chatError: ChatError = { message: apiError.message };
        if (typeof apiError.status === "number") {
          chatError.status = apiError.status;
        }
        setError(chatError);
        setIsLoading(false);
        setIsStreaming(false);
      });
  }, []);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const resetSession = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setMessages([]);
    setError(null);
    setActiveModelId(null);
    setActiveModelName(null);
    setIsLoading(false);
    setIsStreaming(false);
    sessionIdRef.current = generateId();
  }, []);

  return {
    messages,
    isLoading,
    isStreaming,
    error,
    sessionId: sessionIdRef.current,
    activeModelId,
    activeModelName,
    sendMessage: sendMessageHandler,
    resetSession,
  };
}
